<template>
  <BlogArticle
    title="Comment monter son dossier de location en tant qu'auto-entrepreneur ?"
    date="17 février 2025"
    hashtags="auto-entrepreneur, dossier location,dossier locataire"
  >
    <h2 class="fr-h3">
      Guide complet : Constituer votre dossier locatif en tant qu'auto-entrepreneur
    </h2>
    <p>
      Être auto-entrepreneur est une aventure excitante, mais cela peut parfois rendre certaines
      démarches, comme <strong>louer un logement</strong>, un peu plus complexes.
    </p>

    <p>
      En effet, en tant qu'auto-entrepreneur, la constitution d'un dossier de location peut sembler
      être un défi de taille. Les bailleurs exigent des pièces justificatives spécifiques pour
      évaluer la solvabilité et la stabilité financière des candidats.
    </p>

    <p>
      Néanmoins avec les bonnes informations et une préparation adéquate, le processus peut être
      fluide. Dans ce guide pratique , nous allons détailler les pièces justificatives requises afin
      de vous aider à monter votre dossier de location en tant qu'auto-entrepreneur tout en
      rassurant les propriétaires.
    </p>

    <p>
      Le
      <a
        href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000031444493"
        title="décret n° 2015-1437 du 5 novembre 2015 - Ouvre une nouvelle fenêtre"
        target="_blank"
        rel="noopener"
        >décret n° 2015-1437 du 5 novembre 2015</a
      >
      a fixé la liste des pièces à fournir par un candidat locataire et son éventuel garant :
    </p>

    <h3 class="fr-h4">🆔 Pièce d'Identité</h3>
    <p>
      L'une des premières pièces que les bailleurs exigent est une pièce d'identité valide. Vous
      pouvez fournir l'une des options suivantes :
    </p>
    <ul>
      <li>Carte d’identité française ou étrangère (recto verso).</li>
      <li>Passeport (pages 2 et 3).</li>
      <li>Permis de conduire.</li>
      <li>Carte de séjour temporaire ou de résident.</li>
      <li>Carte de ressortissant UE/EEE.</li>
      <li>Document France Identité.</li>
    </ul>

    <p>
      <strong>Assurez-vous de fournir une copie claire et lisible de la pièce choisie.</strong>
    </p>

    <h3 class="fr-h4">🏠 Justificatif de domicile (moins de 3 mois)</h3>
    <p>
      La preuve de votre lieu de résidence est tout aussi cruciale. Vous pouvez utiliser l'un des
      documents suivants, datant de moins de 3 mois :
    </p>
    <ul>
      <li>
        3 dernières quittances ou une attestation de bon paiement des loyers (si vous êtes
        locataire).
      </li>
      <li>
        Attestation sur l’honneur de l’hébergeant, signée et datée, que vous résidez à son domicile
        (si vous êtes hébergé).
      </li>
      <li>Attestation d’élection de domicile.</li>
      <li>
        Dernier avis de taxe foncière, titre de propriété ou attestation notariale (si vous êtes
        propriétaire).
      </li>
      <li>Reçus Airbnb acceptés.</li>
    </ul>

    <h3 class="fr-h4">🧑‍🏫 Justificatif d’activité professionnelle</h3>
    <p>
      En tant qu'auto-entrepreneur, vous pouvez prouver votre activité professionnelle avec l'un des
      documents suivants, datant de moins de 3 mois :
    </p>
    <ul>
      <li>Extrait de situation au répertoire SIRENE.</li>
      <li>Extrait K ou Kbis.</li>
      <li>Certificat d’inscription au SIRENE.</li>
      <li>Attestation d’affiliation à l’URSSAF.</li>
    </ul>

    <h3 class="fr-h4">🪙 Justificatif des ressources</h3>
    <p>
      Certains propriétaires peuvent être inquiets de la stabilité financière des
      auto-entrepreneurs. Pour rassurer les propriétaires sur votre capacité à payer le loyer chaque
      mois, montrez que vos revenus sont réguliers en fournissant l’un des documents suivants,
      datant de moins de 3 mois:
    </p>
    <ul>
      <li>Relevé de situation de l'URSSAF.</li>
      <li>Attestation de chiffre d'affaire ou de ressources d’un expert-comptable.</li>
      <li>
        Attestation de déclaration de chiffre d’affaire de l'URSSAF (mensuelle ou trimestrielle).
      </li>
      <li>Attestation fiscale de l’URSSAF.</li>
      <li>
        Factures représentatives des 3 derniers mois, ce document peut être fourni dans le cas d'un
        début d'activité.
      </li>
    </ul>

    <h3 class="fr-h4">📑 Avis d’imposition</h3>
    <p>
      Les bailleurs exigent souvent un avis d’imposition pour évaluer vos obligations fiscales. Vous
      pouvez fournir :
    </p>
    <ul>
      <li>
        Avis d’imposition français sur les revenus (téléchargeable sur
        <a
          href="https://www.impots.gouv.fr/"
          title="impots.gouv.fr - Ouvre une nouvelle fenêtre"
          target="_blank"
          rel="noopener"
          >impots.gouv.fr</a
        >).
      </li>
      <li>
        Avis d’imposition étranger, à condition que
        <a
          href="https://aide.dossierfacile.logement.gouv.fr/fr/article/simplifier-la-location-pour-les-expatries-traduction-simplifiee-en-francais-1wo4sm8/"
          title="Avis d’imposition étranger, à condition que les parties les plus importantes soient traduites - Ouvre une nouvelle fenêtre"
          target="_blank"
          rel="noopener"
          >les parties les plus importantes soient traduites</a
        >.
      </li>
    </ul>

    <p>
      Pour simplifier et sécuriser le processus de constitution de votre dossier de location en tant
      qu'auto-entrepreneur, vous pouvez envisager d'utiliser
      <a
        href="https://www.dossierfacile.logement.gouv.fr"
        title="DossierFacile - Ouvre une nouvelle fenêtre"
        target="_blank"
        rel="noopener"
        ><strong>DossierFacile</strong></a
      >, la plateforme de l'État qui s'adapte à votre situation grâce à une vérification minutieuse
      de vos documents par des opérateurs spécialisés.
    </p>

    <p>
      Bénéficiez d'une validation rapide sous 24h et d'un filigrane assurant la fiabilité de vos
      pièces. Optez pour <strong>DossierFacile</strong>, pour obtenir un dossier complet, conforme
      et renforcer votre crédibilité auprès des propriétaires, simplifiant ainsi le processus de
      location.
    </p>

    <h3 class="fr-h4">💡 Conseils supplémentaires</h3>
    <ul>
      <li>
        <strong>Préparez une lettre de présentation</strong> : Expliquez votre activité
        d'auto-entrepreneur dans une lettre de présentation. Précisez le type d'activité que vous
        exercez, depuis combien de temps vous êtes en activité, et tout autre détail pertinent qui
        pourrait rassurer le propriétaire quant à votre stabilité financière.
      </li>
      <li>
        <strong>Trouvez un garant</strong> : Si votre activité d’auto-entrepreneur n’en est qu’à ses
        débuts, il est possible que votre dossier personnel ne suffise pas. Vous devez alors miser
        un maximum sur le dossier de vos garants. Dans tous les cas, assurez-vous d’avoir réuni
        toutes les pièces avant de vous lancer dans votre recherche. Il arrive souvent qu’un
        logement passe sous le nez d’un locataire dont le dossier n’était pas prêt assez vite.
      </li>
    </ul>

    <h2 class="fr-h3">🗝️ Conclusion</h2>
    <p>
      La clé du succès pour constituer un dossier de location solide en tant qu'auto-entrepreneur
      réside dans la préparation minutieuse et la présentation claire de chaque pièce justificative.
      Assurez-vous de fournir des documents complets et à jour pour renforcer votre candidature.
      Rendez-vous sur
      <a
        href="https://www.dossierfacile.logement.gouv.fr"
        title="DossierFacile - Ouvre une nouvelle fenêtre"
        target="_blank"
        rel="noopener"
        >DossierFacile</a
      >
      pour constituer votre dossier complet, conforme et cohérent!
    </p>
  </BlogArticle>
</template>

<script setup lang="ts">
import BlogArticle from '@/views/blog/BlogArticle.vue'
import { useSeoMeta } from '@unhead/vue'
import image from '@images/blog-article25.webp'

const title =
  "Dossier de location : Comment monter son dossier de location en tant qu'auto-entrepreneur ?"
const description =
  'Être auto-entrepreneur est une aventure excitante, mais cela peut parfois rendre certaines démarches, comme louer un logement, un peu plus complexes'

const siteTitle = import.meta.env.VITE_SITE_TITLE
const seoTitle = `${title} - ${siteTitle}`

useSeoMeta({
  title: title,
  description: description,
  ogTitle: seoTitle,
  ogImage: image,
  ogDescription: description,
  twitterTitle: seoTitle,
  twitterImage: image,
  twitterDescription: description
})
</script>
